import { useState } from 'react';
import LocationSelect from '../components/shared/LocationSelect.jsx';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { fmtAbv } from '../utils/format.js';
import { uiConfirm } from '../utils/dialogs.js';

// ── Primitives ────────────────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'opacity .15s', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1 };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' }, warn: { background: '#d97706', color: '#fff' } };
  return <button {...props} style={{ ...base, ...v[variant], ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 520, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1 }}>&#215;</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}

function Select({ children, ...props }) {
  return <select {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>;
}

function Textarea(props) {
  return <textarea {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', color: 'var(--text)', ...props.style }} />;
}

function Grid2({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>;
}

function BtnRow({ children }) {
  return <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>{children}</div>;
}

// ── Status colours ────────────────────────────────────────────────────────────
const STATUS_C = {
  filling:     { bg: '#fef9c3', color: '#854d0e' },
  maturing:    { bg: '#dcfce7', color: '#166534' },
  sampled:     { bg: '#ede9fe', color: '#5b21b6' },
  disgorged:   { bg: '#dbeafe', color: '#1e40af' },
  destroyed:   { bg: '#fee2e2', color: '#991b1b' },
  transferred: { bg: '#f3f4f6', color: '#6b7280' },
};

function StatusBadge({ status }) {
  const c = STATUS_C[status] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: c.bg, color: c.color }}>{status?.replace('_', ' ')}</span>;
}

const CASK_TYPES = ['ex-bourbon barrel', 'ex-sherry butt', 'ex-sherry hogshead', 'new oak quarter', 'new oak barrique', 'ex-wine cask', 'STR cask', 'port pipe', 'rum cask', 'other'];
const SPIRIT_TYPES = ['new_make_malt', 'new_make_grain', 'new_make_rye', 'gin_distillate', 'whisky', 'rum', 'brandy', 'other'];

// ── Create Cask modal ─────────────────────────────────────────────────────────
function CreateCaskModal({ onClose, onSaved }) {
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? locData ?? [];
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];

  const [form, setForm] = useState({ cask_type: '', cask_size_litres: '', owner_type: 'PLD', owner_client_id: '', current_location_id: '', bonded: true, notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch('/casks', { method: 'POST', body: { ...form, cask_size_litres: parseFloat(form.cask_size_litres), owner_client_id: form.owner_client_id || undefined, current_location_id: form.current_location_id || undefined } });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Register Cask" onClose={onClose}>
      <form onSubmit={submit}>
        {err && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <Field label="Cask type *">
          <Select value={form.cask_type} onChange={e => set('cask_type')(e.target.value)} required>
            <option value="">Select type...</option>
            {CASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Grid2>
          <Field label="Size (litres) *"><Input type="number" min="1" step="0.01" value={form.cask_size_litres} onChange={e => set('cask_size_litres')(e.target.value)} required placeholder="e.g. 200" /></Field>
          <Field label="Owner">
            <Select value={form.owner_type} onChange={e => set('owner_type')(e.target.value)}>
              <option value="PLD">PLD (own)</option>
              <option value="CONTRACT">Contract client</option>
            </Select>
          </Field>
        </Grid2>
        {form.owner_type === 'CONTRACT' && (
          <Field label="Client">
            <Select value={form.owner_client_id} onChange={e => set('owner_client_id')(e.target.value)}>
              <option value="">Select client...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
        )}
        <Grid2>
          <Field label="Location">
            <LocationSelect locations={locations} value={form.current_location_id} onChange={e => set('current_location_id')(e.target.value)} placeholder="None assigned" />
          </Field>
          <Field label="Duty status">
            <Select value={String(form.bonded)} onChange={e => set('bonded')(e.target.value === 'true')}>
              <option value="true">In bond</option>
              <option value="false">Duty paid</option>
            </Select>
          </Field>
        </Grid2>
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={e => set('notes')(e.target.value)} /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Register Cask'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Fill modal ────────────────────────────────────────────────────────────────
function FillModal({ caskId, onClose, onSaved }) {
  const [form, setForm] = useState({ fill_date: new Date().toISOString().slice(0,10), fill_volume_litres: '', fill_abv: '', fill_lpa: '', fill_spirit_type: '', fill_production_run_id: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => {
    const val = e.target.value;
    setForm(f => {
      const next = { ...f, [k]: val };
      if ((k === 'fill_volume_litres' || k === 'fill_abv') && next.fill_volume_litres && next.fill_abv) {
        next.fill_lpa = (parseFloat(next.fill_volume_litres) * parseFloat(next.fill_abv) / 100).toFixed(4);
      }
      return next;
    });
  };

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch(`/casks/${caskId}/fill`, { method: 'POST', body: { ...form, fill_volume_litres: parseFloat(form.fill_volume_litres), fill_abv: parseFloat(form.fill_abv), fill_lpa: parseFloat(form.fill_lpa), fill_production_run_id: form.fill_production_run_id || undefined } });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Fill Cask" onClose={onClose}>
      <form onSubmit={submit}>
        {err && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <Field label="Fill date *"><Input type="date" value={form.fill_date} onChange={set('fill_date')} required /></Field>
        <Grid2>
          <Field label="Volume (litres) *"><Input type="number" min="0" step="0.001" value={form.fill_volume_litres} onChange={set('fill_volume_litres')} required placeholder="e.g. 185.5" /></Field>
          <Field label="ABV % *"><Input type="number" min="0" max="100" step="0.01" value={form.fill_abv} onChange={set('fill_abv')} required placeholder="e.g. 63.5" /></Field>
        </Grid2>
        <Field label="LPA (auto-calculated)" hint="Litres of pure alcohol — calculated from volume × ABV"><Input type="number" step="0.0001" value={form.fill_lpa} onChange={e => setForm(f => ({ ...f, fill_lpa: e.target.value }))} placeholder="Auto" /></Field>
        <Field label="Spirit type">
          <Select value={form.fill_spirit_type} onChange={e => setForm(f => ({ ...f, fill_spirit_type: e.target.value }))}>
            <option value="">Select...</option>
            {SPIRIT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </Select>
        </Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Filling...' : 'Record Fill'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Sample modal ──────────────────────────────────────────────────────────────
function SampleModal({ caskId, onClose, onSaved }) {
  const [form, setForm] = useState({ sample_date: new Date().toISOString().slice(0,10), volume_taken_litres: '', abv: '', colour: '', nose: '', palate: '', finish: '', recommended_action: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch(`/casks/${caskId}/sample`, { method: 'POST', body: { ...form, volume_taken_litres: parseFloat(form.volume_taken_litres), abv: parseFloat(form.abv) } });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Record Sample" onClose={onClose} width={560}>
      <form onSubmit={submit}>
        {err && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <Grid2>
          <Field label="Sample date *"><Input type="date" value={form.sample_date} onChange={set('sample_date')} required /></Field>
          <Field label="Volume taken (litres) *"><Input type="number" min="0" step="0.001" value={form.volume_taken_litres} onChange={set('volume_taken_litres')} required placeholder="e.g. 0.5" /></Field>
        </Grid2>
        <Grid2>
          <Field label="ABV % *"><Input type="number" min="0" max="100" step="0.01" value={form.abv} onChange={set('abv')} required placeholder="e.g. 61.2" /></Field>
          <Field label="Colour"><Input value={form.colour} onChange={set('colour')} placeholder="e.g. deep amber" /></Field>
        </Grid2>
        <Field label="Nose"><Input value={form.nose} onChange={set('nose')} placeholder="Tasting notes — nose" /></Field>
        <Field label="Palate"><Input value={form.palate} onChange={set('palate')} placeholder="Tasting notes — palate" /></Field>
        <Field label="Finish"><Input value={form.finish} onChange={set('finish')} placeholder="Tasting notes — finish" /></Field>
        <Field label="Recommended action">
          <Select value={form.recommended_action} onChange={set('recommended_action')}>
            <option value="">None specified</option>
            <option value="continue_maturation">Continue maturing</option>
            <option value="ready_for_disgorge">Ready for disgorge</option>
            <option value="disgorge_soon">Disgorge soon</option>
            <option value="further_review">Further review needed</option>
          </Select>
        </Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Record Sample'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Disgorge modal ────────────────────────────────────────────────────────────
function DisgorgeModal({ caskId, onClose, onSaved }) {
  const { data: stockData } = useApi('/stock');
  const stockItems = (stockData?.stock_items ?? stockData ?? []).filter(s => s.category === 'bulk_finished' || s.category === 'semi_product');
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? locData ?? [];

  const [form, setForm] = useState({ operation_date: new Date().toISOString().slice(0,10), volume_litres: '', abv: '', disgorge_to_stock_item_id: '', to_location_id: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch(`/casks/${caskId}/disgorge`, { method: 'POST', body: { ...form, volume_litres: parseFloat(form.volume_litres), abv: parseFloat(form.abv) } });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Disgorge Cask" onClose={onClose} width={560}>
      <form onSubmit={submit}>
        {err && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Disgorging transfers the cask contents to a bulk finished stock item and records a PRODUCTION_OUTPUT movement.</p>
        <Field label="Operation date *"><Input type="date" value={form.operation_date} onChange={set('operation_date')} required /></Field>
        <Grid2>
          <Field label="Volume (litres) *"><Input type="number" min="0" step="0.001" value={form.volume_litres} onChange={set('volume_litres')} required placeholder="e.g. 180.0" /></Field>
          <Field label="ABV % *"><Input type="number" min="0" max="100" step="0.01" value={form.abv} onChange={set('abv')} required placeholder="e.g. 58.4" /></Field>
        </Grid2>
        <Field label="Transfer to stock item *">
          <Select value={form.disgorge_to_stock_item_id} onChange={set('disgorge_to_stock_item_id')} required>
            <option value="">Select stock item...</option>
            {stockItems.map(s => <option key={s.id} value={s.id}>{s.name} ({s.sku})</option>)}
          </Select>
        </Field>
        <Field label="Destination location *">
          <LocationSelect locations={locations} value={form.to_location_id} onChange={set('to_location_id')} required placeholder="Select location..." />
        </Field>
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Disgorging...' : 'Disgorge'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Rerack modal ──────────────────────────────────────────────────────────────
function RerackModal({ caskId, onClose, onSaved }) {
  const { data } = useApi('/casks');
  const otherCasks = (data?.casks ?? []).filter(c => c.id !== caskId && c.status !== 'destroyed' && c.status !== 'disgorged');

  const [form, setForm] = useState({ operation_date: new Date().toISOString().slice(0,10), destination_cask_id: '', volume_litres: '', abv: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch(`/casks/${caskId}/rerack`, { method: 'POST', body: { ...form, volume_litres: form.volume_litres ? parseFloat(form.volume_litres) : undefined, abv: form.abv ? parseFloat(form.abv) : undefined } });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Rerack Cask" onClose={onClose} width={480}>
      <form onSubmit={submit}>
        {err && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <Field label="Date *"><Input type="date" value={form.operation_date} onChange={set('operation_date')} required /></Field>
        <Field label="Destination cask *">
          <Select value={form.destination_cask_id} onChange={set('destination_cask_id')} required>
            <option value="">Select cask...</option>
            {otherCasks.map(c => <option key={c.id} value={c.id}>{c.cask_ref} — {c.cask_type} ({c.cask_size_litres}L)</option>)}
          </Select>
        </Field>
        <Grid2>
          <Field label="Volume (litres)"><Input type="number" min="0" step="0.001" value={form.volume_litres} onChange={set('volume_litres')} placeholder="Optional" /></Field>
          <Field label="ABV %"><Input type="number" min="0" max="100" step="0.01" value={form.abv} onChange={set('abv')} placeholder="Optional" /></Field>
        </Grid2>
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Record Rerack'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Top-up modal ──────────────────────────────────────────────────────────────
function TopUpModal({ caskId, onClose, onSaved }) {
  const [form, setForm] = useState({ operation_date: new Date().toISOString().slice(0,10), volume_litres: '', abv: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch(`/casks/${caskId}/top-up`, { method: 'POST', body: { ...form, volume_litres: parseFloat(form.volume_litres), abv: parseFloat(form.abv) } });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Top Up Cask" onClose={onClose} width={440}>
      <form onSubmit={submit}>
        {err && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <Field label="Date *"><Input type="date" value={form.operation_date} onChange={set('operation_date')} required /></Field>
        <Grid2>
          <Field label="Volume added (litres) *"><Input type="number" min="0" step="0.001" value={form.volume_litres} onChange={set('volume_litres')} required /></Field>
          <Field label="ABV % *"><Input type="number" min="0" max="100" step="0.01" value={form.abv} onChange={set('abv')} required /></Field>
        </Grid2>
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Record Top-up'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Transfer location modal ───────────────────────────────────────────────────
function TransferModal({ caskId, onClose, onSaved }) {
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? locData ?? [];
  const [form, setForm] = useState({ new_location_id: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch(`/casks/${caskId}/transfer-location`, { method: 'POST', body: form });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Transfer Location" onClose={onClose} width={440}>
      <form onSubmit={submit}>
        {err && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <Field label="New location *">
          <LocationSelect locations={locations} value={form.new_location_id} onChange={e => setForm(f => ({ ...f, new_location_id: e.target.value }))} required placeholder="Select location..." />
        </Field>
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Transfer'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Destroy modal ─────────────────────────────────────────────────────────────
function DestroyModal({ cask, onClose, onSaved }) {
  const [form, setForm] = useState({ operation_date: new Date().toISOString().slice(0,10), volume_litres: '', abv: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!(await uiConfirm(`Permanently destroy cask ${cask.cask_ref}? This cannot be undone.`))) return;
    setSaving(true); setErr('');
    try {
      await apiFetch(`/casks/${cask.id}/destroy`, { method: 'POST', body: { ...form, volume_litres: form.volume_litres ? parseFloat(form.volume_litres) : undefined, abv: form.abv ? parseFloat(form.abv) : undefined } });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={`Destroy Cask ${cask.cask_ref}`} onClose={onClose} width={460}>
      <form onSubmit={submit}>
        {err && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <div style={{ padding: '12px 16px', background: '#fef2f2', borderRadius: 'var(--radius)', border: '1px solid #fecaca', marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
          This will mark the cask as destroyed and deactivate it. If spirit remains, a W1 DESTRUCTION movement will be generated.
        </div>
        <Field label="Date *"><Input type="date" value={form.operation_date} onChange={set('operation_date')} required /></Field>
        <Grid2>
          <Field label="Remaining volume (litres)" hint="If any spirit remains"><Input type="number" min="0" step="0.001" value={form.volume_litres} onChange={set('volume_litres')} placeholder="0" /></Field>
          <Field label="ABV %"><Input type="number" min="0" max="100" step="0.01" value={form.abv} onChange={set('abv')} placeholder="If known" /></Field>
        </Grid2>
        <Field label="Reason / notes"><Textarea rows={3} value={form.notes} onChange={set('notes')} placeholder="Reason for destruction" /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn variant="danger" type="submit" disabled={saving}>{saving ? 'Destroying...' : 'Destroy Cask'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Cask detail ───────────────────────────────────────────────────────────────
function CaskDetail({ caskId, onBack }) {
  const { data, loading, refetch } = useApi(`/casks/${caskId}`, [caskId]);
  const { data: opsData,  refetch: refetchOps  } = useApi(`/casks/${caskId}/operations`, [caskId]);
  const { data: sampData, refetch: refetchSamp } = useApi(`/casks/${caskId}/samples`,    [caskId]);

  const cask    = data?.cask    ?? null;
  const ops     = opsData?.operations  ?? [];
  const samples = sampData?.samples    ?? [];

  const [detailTab, setDetailTab] = useState('operations');
  const [modal, setModal] = useState(null); // 'fill'|'sample'|'disgorge'|'rerack'|'topup'|'transfer'|'destroy'

  function closeModal() { setModal(null); }
  function saved()      { refetch(); refetchOps(); refetchSamp(); setModal(null); }

  if (loading || !cask) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading...</div>;

  const active = !['disgorged','destroyed','transferred'].includes(cask.status);
  const filled = !!cask.fill_date;

  const opLabel = { fill: 'Fill', sample: 'Sample', rerack: 'Rerack', disgorge: 'Disgorge', top_up: 'Top-up', destroy: 'Destroy', transfer: 'Transfer' };

  return (
    <div>
      {modal === 'fill'     && <FillModal     caskId={caskId} onClose={closeModal} onSaved={saved} />}
      {modal === 'sample'   && <SampleModal   caskId={caskId} onClose={closeModal} onSaved={saved} />}
      {modal === 'disgorge' && <DisgorgeModal caskId={caskId} onClose={closeModal} onSaved={saved} />}
      {modal === 'rerack'   && <RerackModal   caskId={caskId} onClose={closeModal} onSaved={saved} />}
      {modal === 'topup'    && <TopUpModal    caskId={caskId} onClose={closeModal} onSaved={saved} />}
      {modal === 'transfer' && <TransferModal caskId={caskId} onClose={closeModal} onSaved={saved} />}
      {modal === 'destroy'  && <DestroyModal  cask={cask}    onClose={closeModal} onSaved={() => { saved(); onBack(); }} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 4 }}>&#8592;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'monospace' }}>{cask.cask_ref}</h2>
            <StatusBadge status={cask.status} />
            {cask.bonded && <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8' }}>In Bond</span>}
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {cask.cask_type} &middot; {cask.cask_size_litres}L
            {cask.zone_name ? ` &middot; ${cask.zone_name} — ${cask.location_code}` : ''}
          </p>
        </div>
        {active && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {!filled && <Btn size="sm" onClick={() => setModal('fill')}>Fill</Btn>}
            {filled  && <Btn size="sm" variant="ghost" onClick={() => setModal('sample')}>Sample</Btn>}
            {filled  && <Btn size="sm" variant="ghost" onClick={() => setModal('topup')}>Top-up</Btn>}
            {filled  && <Btn size="sm" variant="ghost" onClick={() => setModal('rerack')}>Rerack</Btn>}
            {filled  && <Btn size="sm" onClick={() => setModal('disgorge')}>Disgorge</Btn>}
            <Btn size="sm" variant="ghost" onClick={() => setModal('transfer')}>Move</Btn>
            <Btn size="sm" variant="danger" onClick={() => setModal('destroy')}>Destroy</Btn>
          </div>
        )}
      </div>

      {/* Fill info card */}
      {filled && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 24 }}>
          {[
            { label: 'Fill date',    value: new Date(cask.fill_date).toLocaleDateString('en-GB') },
            { label: 'Fill volume',  value: `${cask.fill_volume_litres}L` },
            { label: 'Fill ABV',     value: `${fmtAbv(cask.fill_abv)}%` },
            { label: 'Fill LPA',     value: cask.fill_lpa ? Number(cask.fill_lpa).toFixed(3) : '—' },
            { label: 'Spirit type',  value: cask.fill_spirit_type?.replace(/_/g, ' ') || '—' },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {cask.notes && (
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>{cask.notes}</div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {[['operations', `Operations (${ops.length})`], ['samples', `Samples (${samples.length})`]].map(([key, label]) => (
          <button key={key} onClick={() => setDetailTab(key)} style={{ padding: '7px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: detailTab === key ? 700 : 400, color: detailTab === key ? 'var(--pld-green)' : 'var(--text-muted)', borderBottom: detailTab === key ? '2px solid var(--pld-green)' : '2px solid transparent', marginBottom: -1 }}>{label}</button>
        ))}
      </div>

      {/* Operations tab */}
      {detailTab === 'operations' && (
        ops.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No operations recorded yet.</p> : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['Date', 'Operation', 'Volume', 'ABV', 'LPA', 'Notes'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ops.map(op => (
                  <tr key={op.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>{op.operation_date ? new Date(op.operation_date).toLocaleDateString('en-GB') : '—'}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{opLabel[op.operation_type] ?? op.operation_type}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{op.volume_litres ? `${op.volume_litres}L` : '—'}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{op.abv ? `${fmtAbv(op.abv)}%` : '—'}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{op.lpa ? Number(op.lpa).toFixed(3) : '—'}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{op.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Samples tab */}
      {detailTab === 'samples' && (
        samples.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No samples recorded yet.</p> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {samples.map(s => (
              <div key={s.id} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 20px' }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                  <div><span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Date</span><div style={{ fontWeight: 600, marginTop: 2 }}>{new Date(s.sample_date).toLocaleDateString('en-GB')}</div></div>
                  <div><span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Volume taken</span><div style={{ fontWeight: 600, marginTop: 2 }}>{s.volume_taken_litres}L</div></div>
                  <div><span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>ABV</span><div style={{ fontWeight: 600, marginTop: 2 }}>{fmtAbv(s.abv)}%</div></div>
                  <div><span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>LPA removed</span><div style={{ fontWeight: 600, marginTop: 2 }}>{Number(s.lpa_removed).toFixed(3)}</div></div>
                  {s.colour && <div><span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Colour</span><div style={{ marginTop: 2 }}>{s.colour}</div></div>}
                  {s.recommended_action && <div><span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Action</span><div style={{ marginTop: 2, textTransform: 'capitalize' }}>{s.recommended_action.replace(/_/g, ' ')}</div></div>}
                </div>
                {(s.nose || s.palate || s.finish) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                    {s.nose   && <div><strong style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>Nose</strong> — {s.nose}</div>}
                    {s.palate && <div><strong style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>Palate</strong> — {s.palate}</div>}
                    {s.finish && <div><strong style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>Finish</strong> — {s.finish}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ── Cask list ─────────────────────────────────────────────────────────────────
const STATUS_FILTERS = ['all', 'maturing', 'sampled', 'disgorged', 'destroyed'];

export default function Casks() {
  const [statusFilter, setStatusFilter]   = useState('maturing');
  const [ownerFilter,  setOwnerFilter]    = useState('all');
  const [bondedFilter, setBondedFilter]   = useState('all');
  const [selectedId,   setSelectedId]     = useState(null);
  const [showCreate,   setShowCreate]     = useState(false);

  const qs = [
    statusFilter !== 'all' ? `status=${statusFilter}` : '',
    ownerFilter  !== 'all' ? `owner_type=${ownerFilter}` : '',
    bondedFilter !== 'all' ? `bonded=${bondedFilter}` : '',
  ].filter(Boolean).join('&');

  const { data, loading, refetch } = useApi(`/casks${qs ? '?' + qs : ''}`, [statusFilter, ownerFilter, bondedFilter]);
  const casks = data?.casks ?? [];

  if (selectedId) return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      <CaskDetail caskId={selectedId} onBack={() => { setSelectedId(null); refetch(); }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      {showCreate && <CreateCaskModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Casks</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Maturation register — fill, sample, disgorge and track all casks</p>
        </div>
        <Btn onClick={() => setShowCreate(true)}>+ Register Cask</Btn>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {STATUS_FILTERS.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: statusFilter === s ? 'var(--pld-green)' : 'var(--surface-2)', color: statusFilter === s ? '#fff' : 'var(--text-muted)' }}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} style={{ padding: '5px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface-2)', color: 'var(--text)' }}>
          <option value="all">All owners</option>
          <option value="PLD">PLD only</option>
          <option value="CONTRACT">Client casks</option>
        </select>
        <select value={bondedFilter} onChange={e => setBondedFilter(e.target.value)} style={{ padding: '5px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface-2)', color: 'var(--text)' }}>
          <option value="all">Bonded &amp; duty-paid</option>
          <option value="true">In bond only</option>
          <option value="false">Duty-paid only</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p>
      ) : casks.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
          <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>No casks found</p>
          <p style={{ fontSize: 13, margin: 0 }}>Register your first cask to start tracking maturation.</p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Ref', 'Type', 'Size', 'Fill date', 'Fill LPA', 'Location', 'Bond', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {casks.map(c => (
                <tr key={c.id} onClick={() => setSelectedId(c.id)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '11px 14px', fontWeight: 700, fontFamily: 'monospace' }}>{c.cask_ref}</td>
                  <td style={{ padding: '11px 14px' }}>{c.cask_type}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{c.cask_size_litres}L</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{c.fill_date ? new Date(c.fill_date).toLocaleDateString('en-GB') : '—'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{c.fill_lpa ? Number(c.fill_lpa).toFixed(3) : '—'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{c.zone_name ? `${c.zone_name} / ${c.location_code}` : '—'}</td>
                  <td style={{ padding: '11px 14px' }}>{c.bonded ? <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', padding: '2px 7px', borderRadius: 99 }}>Bond</span> : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>D/P</span>}</td>
                  <td style={{ padding: '11px 14px' }}><StatusBadge status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
